(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const body = document.body;
  const rootElement = document.documentElement;
  const routeRoot = (window.Werlar && window.Werlar.root) || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 960px)');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let activeOverlay = null;
  let overlayReturnFocus = null;
  let cleanupHeaderController = null;
  const megaCloseTimers = new WeakMap();

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null;
    });
  }

  function anyOverlayOpen() {
    return Boolean(document.querySelector('.wr-search-drawer.is-open,.wr-cart-drawer.is-open,.wr-mobile-drawer.is-open'));
  }

  function syncBodyLock() {
    body.classList.toggle('wr-overlay-open', anyOverlayOpen());
  }

  function activateOverlay(overlay, trigger, preferredFocus) {
    if (!overlay) return;
    activeOverlay = overlay;
    overlayReturnFocus = trigger || document.activeElement;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    syncBodyLock();
    window.setTimeout(() => {
      const target = preferredFocus || overlay.querySelector('[autofocus]') || getFocusable(overlay)[0] || overlay.querySelector('[data-wr-modal-panel]');
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    }, reducedMotion.matches ? 0 : 120);
  }

  function deactivateOverlay(overlay, triggerSelector) {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    if (triggerSelector) document.querySelectorAll(triggerSelector).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    const shouldRestore = activeOverlay === overlay;
    activeOverlay = null;
    syncBodyLock();
    if (shouldRestore && overlayReturnFocus && document.contains(overlayReturnFocus)) {
      overlayReturnFocus.focus({ preventScroll: true });
    }
    overlayReturnFocus = null;
  }

  function cancelMegaClose(item) {
    if (!item) return;
    const timer = megaCloseTimers.get(item);
    if (timer) window.clearTimeout(timer);
    megaCloseTimers.delete(item);
  }

  function setMegaMenuState(item, open) {
    if (!item) return;
    cancelMegaClose(item);
    item.classList.toggle('is-open', open);
    const button = item.querySelector('[data-wr-mega-toggle]');
    const menu = item.querySelector('[data-wr-mega-menu]');
    if (button) button.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (menu) menu.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  function closeMegaMenus(exceptItem) {
    document.querySelectorAll('[data-wr-nav-item].is-open').forEach((item) => {
      if (item === exceptItem) return;
      setMegaMenuState(item, false);
    });
  }

  function scheduleMegaClose(item, delay = 320) {
    if (!item) return;
    cancelMegaClose(item);
    const timer = window.setTimeout(() => {
      megaCloseTimers.delete(item);
      if (item.matches(':hover') || item.contains(document.activeElement)) return;
      setMegaMenuState(item, false);
    }, delay);
    megaCloseTimers.set(item, timer);
  }

  function initHeaderController() {
    if (cleanupHeaderController) cleanupHeaderController();

    const headerStack = document.querySelector('[data-wr-header-stack]');
    const headerSection = headerStack ? headerStack.closest('.shopify-section') : null;
    if (!headerStack || !headerSection) return;

    const behavior = headerStack.dataset.headerBehavior || 'always';
    let lastY = Math.max(0, window.scrollY);
    let ticking = false;
    let resizeObserver = null;

    body.classList.remove('wr-header-always', 'wr-header-scroll-up', 'wr-header-static', 'wr-header-is-hidden');
    body.classList.add(behavior === 'scroll_up' ? 'wr-header-scroll-up' : behavior === 'static' ? 'wr-header-static' : 'wr-header-always');
    headerSection.classList.add('wr-header-section');

    function headerHeight() {
      return Math.ceil(headerStack.getBoundingClientRect().height || headerStack.offsetHeight || 0);
    }

    function syncMetrics() {
      const full = headerHeight();
      const hidden = behavior === 'scroll_up' && body.classList.contains('wr-header-is-hidden');
      const visible = behavior === 'static' || hidden ? 0 : full;
      rootElement.style.setProperty('--wr-header-height', `${full}px`);
      rootElement.style.setProperty('--wr-visible-header-height', `${visible}px`);
    }

    function onScroll() {
      if (behavior !== 'scroll_up' || ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = Math.max(0, window.scrollY);
        const delta = y - lastY;
        const threshold = mobileQuery.matches ? 10 : 6;
        const past = Math.max(headerHeight() + 24, 110);

        if (y < 16) {
          body.classList.remove('wr-header-is-hidden');
        } else if (delta > threshold && y > past && !body.classList.contains('wr-overlay-open')) {
          body.classList.add('wr-header-is-hidden');
        } else if (delta < -threshold) {
          body.classList.remove('wr-header-is-hidden');
        }
        syncMetrics();
        lastY = y;
        ticking = false;
      });
    }

    function onResize() {
      body.classList.remove('wr-header-is-hidden');
      lastY = Math.max(0, window.scrollY);
      syncMetrics();
    }

    syncMetrics();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', syncMetrics, { once: true });
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(syncMetrics);
      resizeObserver.observe(headerStack);
    }

    cleanupHeaderController = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (resizeObserver) resizeObserver.disconnect();
      cleanupHeaderController = null;
    };
  }

  function moveViewportOverlays() {
    function keepNewest(id) {
      const matches = Array.from(document.querySelectorAll(`#${id}`));
      if (!matches.length) return null;
      const current = matches[0];
      matches.slice(1).forEach((duplicate) => duplicate.remove());
      if (current.parentElement !== body) body.appendChild(current);
      return current;
    }
    keepNewest('WerlarMobileMenu');
    keepNewest('WerlarSearchDrawer');
  }

  function openMobileMenu(trigger) {
    const mobile = document.getElementById('WerlarMobileMenu');
    if (!mobile) return;
    activateOverlay(mobile, trigger, mobile.querySelector('.js-wr-menu-close'));
  }

  function closeMobileMenu() {
    deactivateOverlay(document.getElementById('WerlarMobileMenu'), '.js-wr-menu-open');
  }

  function openSearch(trigger) {
    const search = document.getElementById('WerlarSearchDrawer');
    if (!search) return;
    activateOverlay(search, trigger, search.querySelector('#WerlarSearchInput'));
  }

  function closeSearch() {
    deactivateOverlay(document.getElementById('WerlarSearchDrawer'), '.js-search-trigger');
  }

  function initSkeletons(scope) {
    if (!(window.Werlar && window.Werlar.skeletons)) return;
    (scope || document).querySelectorAll('[data-wr-skeleton]:not([data-wr-skeleton-ready])').forEach((shell) => {
      shell.setAttribute('data-wr-skeleton-ready', 'true');
      const media = shell.querySelector('img,video');
      if (!media) {
        shell.classList.add('is-loaded');
        return;
      }
      const markLoaded = () => shell.classList.add('is-loaded');
      if ((media.tagName === 'IMG' && media.complete) || (media.tagName === 'VIDEO' && media.readyState >= 2)) {
        markLoaded();
      } else {
        media.addEventListener('load', markLoaded, { once: true });
        media.addEventListener('loadeddata', markLoaded, { once: true });
        media.addEventListener('error', markLoaded, { once: true });
      }
    });
  }

  function initBackToTop() {
    const button = document.querySelector('[data-wr-back-to-top]');
    if (!button || button.dataset.initialized === 'true') return;
    button.dataset.initialized = 'true';
    let ticking = false;

    function update() {
      const visible = window.scrollY > 650;
      button.hidden = false;
      button.classList.toggle('is-visible', visible);
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');
      button.tabIndex = visible ? 0 : -1;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
    button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' }));
    update();
  }

  function initializeGlobalFeatures(scope) {
    moveViewportOverlays();
    initHeaderController();
    initSkeletons(scope || document);
    initBackToTop();
  }

  document.addEventListener('click', (event) => {
    const menuTrigger = event.target.closest('.js-wr-menu-open');
    if (menuTrigger) {
      event.preventDefault();
      openMobileMenu(menuTrigger);
      return;
    }

    if (event.target.closest('.js-wr-menu-close')) {
      event.preventDefault();
      closeMobileMenu();
      return;
    }

    const mobileOverlay = event.target.closest('#WerlarMobileMenu');
    if (mobileOverlay && event.target === mobileOverlay) {
      closeMobileMenu();
      return;
    }

    const searchTrigger = event.target.closest('.js-search-trigger');
    if (searchTrigger) {
      event.preventDefault();
      openSearch(searchTrigger);
      return;
    }

    if (event.target.closest('.js-search-close')) {
      event.preventDefault();
      closeSearch();
      return;
    }

    const megaGroupToggle = event.target.closest('[data-wr-mega-group-toggle]');
    if (megaGroupToggle) {
      event.preventDefault();
      event.stopPropagation();
      const group = megaGroupToggle.closest('[data-wr-mega-group]');
      const groups = megaGroupToggle.closest('[data-wr-mega-groups]');
      const panel = document.getElementById(megaGroupToggle.getAttribute('aria-controls'));
      const open = megaGroupToggle.getAttribute('aria-expanded') !== 'true';

      if (open && groups && groups.dataset.singleOpen === 'true') {
        groups.querySelectorAll('[data-wr-mega-group-toggle][aria-expanded="true"]').forEach((button) => {
          if (button === megaGroupToggle) return;
          button.setAttribute('aria-expanded', 'false');
          const otherPanel = document.getElementById(button.getAttribute('aria-controls'));
          if (otherPanel) otherPanel.hidden = true;
          const otherGroup = button.closest('[data-wr-mega-group]');
          if (otherGroup) otherGroup.classList.remove('is-expanded');
        });
      }

      megaGroupToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
      if (group) group.classList.toggle('is-expanded', open);
      return;
    }

    const megaToggle = event.target.closest('[data-wr-mega-toggle]');
    if (megaToggle) {
      event.preventDefault();
      const item = megaToggle.closest('[data-wr-nav-item]');
      const menu = item && item.querySelector('[data-wr-mega-menu]');
      const open = item && !item.classList.contains('is-open');
      closeMegaMenus(open ? item : null);
      if (item && menu) setMegaMenuState(item, open);
      return;
    }

    const mobileSubmenuToggle = event.target.closest('[data-wr-mobile-submenu-toggle]');
    if (mobileSubmenuToggle) {
      event.preventDefault();
      const submenu = document.getElementById(mobileSubmenuToggle.getAttribute('aria-controls'));
      const open = mobileSubmenuToggle.getAttribute('aria-expanded') !== 'true';
      mobileSubmenuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (submenu) submenu.hidden = !open;
      return;
    }

    if (!event.target.closest('[data-wr-nav-item]')) closeMegaMenus();

    const mobileLink = event.target.closest('#WerlarMobileMenu a');
    if (mobileLink && !mobileLink.closest('.wr-mobile-submenu-toggle')) closeMobileMenu();
  });

  document.addEventListener('focusin', (event) => {
    const item = event.target.closest('[data-wr-nav-item].has-mega');
    if (!item) return;
    closeMegaMenus(item);
    setMegaMenuState(item, true);
  });

  document.addEventListener('focusout', (event) => {
    const item = event.target.closest('[data-wr-nav-item].has-mega');
    if (!item || item.contains(event.relatedTarget)) return;
    scheduleMegaClose(item, 180);
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const item = event.target.closest('[data-wr-nav-item].has-mega');
    if (!item) return;
    cancelMegaClose(item);
    if (item.contains(event.relatedTarget)) return;
    closeMegaMenus(item);
    setMegaMenuState(item, true);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const item = event.target.closest('[data-wr-nav-item].has-mega');
    if (!item || item.contains(event.relatedTarget)) return;
    scheduleMegaClose(item);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const openMega = document.querySelector('[data-wr-nav-item].is-open');
      if (openMega) {
        const toggle = openMega.querySelector('[data-wr-mega-toggle]');
        closeMegaMenus();
        if (toggle) toggle.focus();
        return;
      }
      if (document.getElementById('WerlarSearchDrawer')?.classList.contains('is-open')) {
        closeSearch();
        return;
      }
      if (document.getElementById('WerlarMobileMenu')?.classList.contains('is-open')) {
        closeMobileMenu();
        return;
      }
      if (window.WerlarCart) window.WerlarCart.close();
      return;
    }

    if (event.key === 'Tab' && activeOverlay && activeOverlay.classList.contains('is-open')) {
      const panel = activeOverlay.querySelector('[data-wr-modal-panel]') || activeOverlay;
      const focusables = getFocusable(panel);
      if (!focusables.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener('shopify:section:load', (event) => initializeGlobalFeatures(event.target));
  document.addEventListener('shopify:section:reorder', () => initializeGlobalFeatures(document));
  document.addEventListener('shopify:section:select', initHeaderController);

  initializeGlobalFeatures(document);

  /* Cart drawer: delegated events survive every Shopify section refresh. */
  (() => {
    const mount = document.getElementById('CartDrawerMount');
    if (!mount) return;
    let busy = false;

    function drawer() { return mount.querySelector('.js-cart-drawer'); }
    function panel() { return mount.querySelector('.js-cart-drawer__panel'); }
    function setBusy(on) {
      busy = on;
      const currentPanel = panel();
      if (currentPanel) {
        currentPanel.classList.toggle('is-loading', on);
        currentPanel.setAttribute('aria-busy', on ? 'true' : 'false');
      }
    }

    function open(trigger) {
      const currentDrawer = drawer();
      if (!currentDrawer) return;
      currentDrawer.querySelector('.js-cart-drawer__panel')?.setAttribute('data-wr-modal-panel', '');
      activateOverlay(currentDrawer, trigger, currentDrawer.querySelector('.js-cart-drawer-close'));
      document.querySelectorAll('.js-cart-trigger').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    }

    function close() {
      deactivateOverlay(drawer(), '.js-cart-trigger');
    }

    function updateHeaderCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = count;
        element.hidden = count === 0;
      });
    }

    async function refresh(openAfter = true) {
      const response = await fetch(`${routeRoot}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to refresh cart');
      const data = await response.json();
      if (!data['cart-drawer']) throw new Error('Cart drawer section was not returned');
      mount.innerHTML = data['cart-drawer'];
      initSkeletons(mount);
      const count = Number(mount.querySelector('[data-drawer-count]')?.dataset.drawerCount || 0);
      updateHeaderCount(count);
      if (openAfter) open(document.querySelector('.js-cart-trigger'));
    }

    async function change(itemKey, line, quantity) {
      if (busy) return;
      const nextQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
      setBusy(true);
      try {
        const response = await fetch(`${routeRoot}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(itemKey ? { id: itemKey, quantity: nextQuantity } : { line: Number(line), quantity: nextQuantity })
        });
        if (!response.ok) {
          let message = 'Unable to update cart';
          try {
            const errorData = await response.json();
            message = errorData.description || errorData.message || message;
          } catch (_error) {
            /* Keep the fallback message. */
          }
          throw new Error(message);
        }
        await refresh(true);
      } catch (error) {
        console.error(error);
        window.alert(error.message || 'The cart could not be updated. Please try again.');
      } finally {
        setBusy(false);
      }
    }

    document.addEventListener('click', (event) => {
      const cartTrigger = event.target.closest('.js-cart-trigger');
      if (cartTrigger) {
        event.preventDefault();
        open(cartTrigger);
        return;
      }
      if (event.target.closest('.js-cart-drawer-close,.js-cart-drawer__overlay')) {
        event.preventDefault();
        close();
        return;
      }
      const remove = event.target.closest('.js-drawer-remove');
      if (remove) {
        event.preventDefault();
        change(remove.dataset.itemKey, remove.dataset.line, 0);
        return;
      }
      const step = event.target.closest('.js-drawer-qty-step');
      if (step) {
        event.preventDefault();
        if (busy) return;
        const escapedKey = window.CSS && CSS.escape ? CSS.escape(step.dataset.itemKey || '') : (step.dataset.itemKey || '').replace(/["\\]/g, '\\$&');
        const selector = step.dataset.itemKey ? `.js-drawer-qty[data-item-key="${escapedKey}"]` : `.js-drawer-qty[data-line="${step.dataset.line}"]`;
        const input = mount.querySelector(selector);
        if (!input) return;
        const current = Math.max(0, Number.parseInt(input.value, 10) || 0);
        const next = Math.max(0, current + (Number.parseInt(step.dataset.delta, 10) || 0));
        input.value = next;
        change(step.dataset.itemKey, step.dataset.line, next);
      }
    });

    mount.addEventListener('change', (event) => {
      const input = event.target.closest('.js-drawer-qty');
      if (input) change(input.dataset.itemKey, input.dataset.line, input.value);
    });

    mount.addEventListener('keydown', (event) => {
      const input = event.target.closest('.js-drawer-qty');
      if (input && event.key === 'Enter') {
        event.preventDefault();
        input.blur();
      }
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches('form[action*="/cart/add"]') || form.dataset.cartBypass === 'true') return;
      event.preventDefault();
      if (busy) return;
      setBusy(true);
      let itemAdded = false;
      try {
        const response = await fetch(`${routeRoot}cart/add.js`, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Unable to add item');
        itemAdded = true;
        await refresh(true);
      } catch (error) {
        console.error(error);
        if (itemAdded) {
          // The add succeeded: recover the drawer by opening the cart, without replaying the add.
          window.location.assign(`${routeRoot}cart`);
        } else {
          // A failed network response can leave the add result uncertain. Do not submit again.
          window.alert('We could not confirm the cart update. Please check your cart before trying again.');
        }
      } finally {
        setBusy(false);
      }
    });

    window.WerlarCart = { open, close, refresh };
  })();
})();
